// New enquiries (the Messages screen's lead rows) — the pure half. Same shape
// as rebook-message.ts: the wording lives here and nowhere else, and a test
// pins it verbatim. A lead has no cycle to reset the count, so unlike a
// customer the enquiry message count is the lead's whole history.

import { mailtoHref } from './mailto'
import type { NoteChannel } from './contact'
import { firstName } from './late-message'
import { leadIsOpen } from './new-jobs'
import type { Lead } from './types'
import { businessSettings, wordMessage, type BusinessSettings } from './business-settings'
import { mailSubject } from './trade'

// The first reply to a brand-new enquiry: price the job from photos or a
// window count (Liam prices from photos where he can — no visit offered).
//
// settings takes its words from the signed-in business (businessSettings(),
// filled at boot from /api/me) — no Dropby fallback.
export function enquiryFirstMessage(name: string | null | undefined, settings: BusinessSettings = businessSettings()): string {
  return wordMessage('enquiry_first', { 'first name': firstName(name), 'your name': settings.booking_signoff }, settings)
}

// The chase, ENQUIRY_SECOND_AFTER_DAYS after the first with no reply. It
// chases the same thing the first asked for — the photos or the count — not
// the visit the first message no longer offers.
export function enquirySecondMessage(name: string | null | undefined, settings: BusinessSettings = businessSettings()): string {
  return wordMessage('enquiry_second', { 'first name': firstName(name), 'your name': settings.booking_signoff }, settings)
}

// The one nudge after a QUOTE (New jobs redesign, Liam 10 Oct 2026): about the quote they have, softly, not the photos
// the first reply asked for. A job with a price saved, or marked quoted, has had its quote.
export function quoteNudgeMessage(name: string | null | undefined, settings: BusinessSettings = businessSettings()): string {
  return wordMessage('quote_nudge', { 'first name': firstName(name), 'your name': settings.booking_signoff }, settings)
}
export function quoted(l: Pick<Lead, 'status'> & Partial<Pick<Lead, 'regular_pence' | 'initial_pence'>>): boolean {
  return l.status === 'quote' || l.regular_pence != null || l.initial_pence != null
}
// The second message for this enquiry: the quote nudge once quoted, else the photos chase.
export function nudgeMessageFor(l: Pick<Lead, 'name' | 'status'> & Partial<Pick<Lead, 'regular_pence' | 'initial_pence'>>, settings: BusinessSettings = businessSettings()): string {
  return quoted(l) ? quoteNudgeMessage(l.name, settings) : enquirySecondMessage(l.name, settings)
}

// Enquiries get less patience than overdue customers: five days, not seven.
// Only recent enquiries count as "new": older quotes are a tidy-up job for
// New jobs, not a daily reply queue (the first live view listed 40, back to
// September 2025).
export const ENQUIRY_FRESH_DAYS = 30
export const ENQUIRY_SECOND_AFTER_DAYS = 5

// The take-off rule, same count as the customers' ladder: two messages with
// no reply.
export const ENQUIRY_NO_REPLY_AFTER = 2

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// new = nobody has replied to the enquiry yet; then the same ladder the
// customer rows run: waiting → second → no_reply.
export type EnquiryStage = 'new' | 'waiting' | 'second' | 'no_reply'

// The day the second message unlocks, or null when there is nothing to wait for.
export function enquirySecondFrom(l: { enquiry_messaged_on?: string | null }): string | null {
  return l.enquiry_messaged_on ? addDays(l.enquiry_messaged_on, ENQUIRY_SECOND_AFTER_DAYS) : null
}

// How many enquiry messages have gone out (the stored count; no cycle reset).
export function enquiryMessages(l: { enquiry_messages?: number | null }): number {
  return Math.max(0, Math.round(Number(l.enquiry_messages) || 0))
}

export function enquiryStageOf(l: { enquiry_messages?: number | null; enquiry_messaged_on?: string | null }, today: string): EnquiryStage {
  const n = enquiryMessages(l)
  if (n === 0) return 'new'
  if (n >= ENQUIRY_NO_REPLY_AFTER) return 'no_reply'
  const from = enquirySecondFrom(l)
  return from && today >= from ? 'second' : 'waiting'
}

// The line appended to the lead's contact note: "11 Sep: enquiry WhatsApp 1",
// or with the channel it went on: "11 Sep: enquiry email 1". The channel is
// what the put-back below matches on, so it can drop a line whichever way the
// reply went.
export function enquiryNoteLine(dayLabel: string, n: number, channel: NoteChannel = 'WhatsApp'): string {
  return `${dayLabel}: enquiry ${channel} ${n}`
}

// The email reply on an enquiry row (Liam 2026-09-17: "Also do a email icon in
// case we have their email address"): his mail app, to their address, with the
// SAME first message as the body — one reply, one place it is written. The
// subject names the enquiry so his Sent folder reads back. null unless the
// address looks like one: exactly one @ with a dot after it, no spaces — a
// half-typed address must not silently become a dead link.
export function enquiryEmailHref(email: string | null | undefined, name: string | null | undefined): string | null {
  // One plain mailbox only (contact.ts EMAIL_RULE): an imported "a@b.com?cc=…" must not add to the link.
  return mailtoHref((email ?? '').trim().toLowerCase(), mailSubject('enquiry'), enquiryFirstMessage(name))
}

// "Didn't send it? Put them back": the fields that take ONE enquiry message
// off the lead again. Written once so Messages and the diary's week panel
// undo a tap the same way. The count drops by one, the date clears when no
// message is left, and the last "…: enquiry WhatsApp N" (or email N) note
// line goes. The "last contacted" date the tap stamped goes too when it was
// that tap: left in place it hid the enquiry from both lists for two days,
// so a put-back made them vanish instead of coming back (found 2026-09-17).
export function enquiryUncountFields(l: { enquiry_messages?: number | null; enquiry_messaged_on?: string | null; contact_note?: string | null; last_contacted_on?: string | null }): {
  enquiry_messages: number
  enquiry_messaged_on: string | null
  contact_note: string | null
  last_contacted_on: string | null
} {
  const n = Math.max(0, enquiryMessages(l) - 1)
  const noteLines = (l.contact_note ?? '').split('\n')
  const lastIdx = noteLines.map((line) => /: enquiry (WhatsApp|text|email) \d+$/.test(line)).lastIndexOf(true)
  const note = noteLines.filter((_, i) => i !== lastIdx).join('\n')
  const stampedByTap = !!l.enquiry_messaged_on && l.last_contacted_on === l.enquiry_messaged_on
  return {
    enquiry_messages: n,
    enquiry_messaged_on: n ? l.enquiry_messaged_on ?? null : null,
    contact_note: note || null,
    last_contacted_on: n === 0 && stampedByTap ? null : l.last_contacted_on ?? null,
  }
}

// Messaged today and only once: the tap may not have become a real message,
// so the week panel keeps the row in view with a put-back for the day.
export function enquiryMessagedToday(l: { enquiry_messages?: number | null; enquiry_messaged_on?: string | null }, today: string): boolean {
  return enquiryMessages(l) === 1 && l.enquiry_messaged_on === today
}

// ---- old quotes: one last try, or remove (Liam 2026-09-28) ----------------------
// Quotes over a month old that were never messaged sit on no list (39 of 42 open ones on 28 Sep, back
// to Sep 2025). Liam: "an option so i can try one last time ... and an easy to delete button too". The
// wording is the one he approved, in his voice: their side first, no re-describing the quote.
// The last message after two with no reply (Liam, 28 Oct 2026, "go with A"): sending it takes the enquiry off.
export function enquiryFinalMessage(name: string | null | undefined, settings: BusinessSettings = businessSettings()): string {
  return wordMessage('enquiry_final', { 'first name': firstName(name), 'your name': settings.booking_signoff }, settings)
}

export function enquiryLastTryMessage(name: string | null | undefined, settings: BusinessSettings = businessSettings()): string {
  return wordMessage('enquiry_last', { 'first name': firstName(name), 'your name': settings.booking_signoff }, settings)
}

// The same last try by email, when the job has an address that looks like one.
export function lastTryEmailHref(email: string | null | undefined, name: string | null | undefined): string | null {
  return mailtoHref((email ?? '').trim().toLowerCase(), mailSubject('quote'), enquiryLastTryMessage(name))
}

// The note line a last try leaves ("28 Sep: last try WhatsApp"). It is NOT an enquiry message: the
// count stays at nought, so the quote stays on the old quotes list, marked, instead of starting the
// new-enquiry ladder with its photos-please second message.
export function lastTryNoteLine(dayLabel: string, channel: NoteChannel = 'WhatsApp'): string {
  return `${dayLabel}: last try ${channel}`
}

// When the last try went, as its own note line wrote it ("28 Sep"), or null if it has not. Read from
// the line itself, not "last contacted", which a later call moves (Codex review 2026-09-28).
export function lastTrySentOn(l: { contact_note?: string | null }): string | null {
  const lines = (l.contact_note ?? '').split('\n')
  for (let i = lines.length - 1; i >= 0; i--) {
    const m = /^(.+): last try (WhatsApp|text|email)$/.exec(lines[i]!.trim())
    if (m) return m[1]!
  }
  return null
}

// Old quotes (Liam 2026-09-28): open quotes over a month old that were never messaged, so no other
// group on Messages holds them, and undated ones. Newest first: the likeliest to still say yes. Not
// counted in the nav badge; like Lapsed, a pile to chip away at.
export function oldQuotes<T extends Pick<Lead, 'id' | 'status' | 'status_raw' | 'quoted_on' | 'enquiry_messages'>>(leads: T[], today: string): T[] {
  const oldest = addDays(today, -ENQUIRY_FRESH_DAYS)
  return leads
    .filter(leadIsOpen)
    .filter((l) => enquiryMessages(l) === 0)
    .filter((l) => !l.quoted_on || l.quoted_on < oldest)
    .sort((a, b) => (b.quoted_on ?? '').localeCompare(a.quoted_on ?? '') || b.id - a.id)
}
